// ui/src/lib/logger/transports/remote.ts

import { isLoggerRateLimitDisabled } from "../runtimeConfig";
import { sanitizeLogData } from "../sanitize";
import { ITransport, LogLevel, LogMessage } from "../types";

const PUBLIC_AUTH_ROUTE_PREFIXES = [
  "/sign-in",
  "/sign-up",
  "/auth",
  "/reset-password",
  "/new-password",
  "/2fa"
] as const;

function isPublicAuthPathname(pathname: string): boolean {
  return PUBLIC_AUTH_ROUTE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

type RemoteTransportOptions = {
  maxBatchSize?: number;
  maxQueueSize?: number;
  fetchTimeoutMs?: number;
  baseBackoffMs?: number;
  maxBackoffMs?: number;
  jitterRatio?: number;
  cooldownAfterFailures?: number;
  cooldownMs?: number;
  consoleLogThrottleMs?: number;
  suppressBootstrapNoise?: boolean;
  disableRateLimit?: boolean;
};

type RemoteLogBatchEntry = {
  level: LogLevel;
  message: unknown;
  context: Record<string, unknown>;
  timestamp: string;
  pathname?: string;
};

export class RemoteTransport implements ITransport {
  private readonly endpoint: string;
  private readonly queue: RemoteLogBatchEntry[] = [];
  private isSending = false;
  private isBrowser: boolean;
  private readonly MAX_LOG_SIZE = 100 * 1024; // 100KB
  private requestCount = 0;
  private lastResetTime = Date.now();

  private readonly opts: Required<RemoteTransportOptions>;
  private consecutiveFailures = 0;
  private nextAttemptAtMs = 0;
  private retryTimer: ReturnType<typeof setTimeout> | undefined;
  private lastConsoleLogAtMs = 0;

  constructor(endpoint: string, options: RemoteTransportOptions = {}) {
    this.endpoint = endpoint;
    this.isBrowser = typeof window !== "undefined" && typeof fetch !== "undefined";

    this.opts = {
      maxBatchSize: options.maxBatchSize ?? 10,
      maxQueueSize: options.maxQueueSize ?? 500,
      fetchTimeoutMs: options.fetchTimeoutMs ?? 5000,
      baseBackoffMs: options.baseBackoffMs ?? 1000,
      maxBackoffMs: options.maxBackoffMs ?? 60_000,
      jitterRatio: options.jitterRatio ?? 0.25,
      cooldownAfterFailures: options.cooldownAfterFailures ?? 6,
      cooldownMs: options.cooldownMs ?? 5 * 60_000,
      consoleLogThrottleMs: options.consoleLogThrottleMs ?? 30_000,
      suppressBootstrapNoise: options.suppressBootstrapNoise ?? false,
      disableRateLimit: options.disableRateLimit ?? isLoggerRateLimitDisabled()
    };
  }

  private getCurrentPathname(): string | undefined {
    if (typeof window === "undefined" || !window.location?.pathname) return undefined;
    return window.location.pathname;
  }

  private shouldSuppressForBootstrap(pathname: string | undefined, level: LogLevel): boolean {
    if (!this.opts.suppressBootstrapNoise || !pathname) return false;
    if (!isPublicAuthPathname(pathname)) return false;
    return level === "debug" || level === "info";
  }

  log(entry: LogMessage): void {
    if (!this.isBrowser) return;

    const pathname = this.getCurrentPathname();
    if (this.shouldSuppressForBootstrap(pathname, entry.level as LogLevel)) {
      return;
    }

    if (!this.opts.disableRateLimit) {
      // Rate limiting
      const now = Date.now();
      if (now - this.lastResetTime > 60 * 1000) {
        this.requestCount = 0;
        this.lastResetTime = now;
      }

      if (this.requestCount >= 1000) {
        // 1000 requests per minute
        console.warn(`[LOGGER] Dropped log due to rate limit: 1000 logs/min exceeded.`);
        return;
      }
    }

    // Sanitize the log data
    const sanitizedContext = sanitizeLogData(entry);
    const sanitizedContextObj =
      typeof sanitizedContext === "object" && sanitizedContext !== null
        ? (sanitizedContext as Record<string, unknown>)
        : {};
    const sanitizedMessage = typeof entry.message === "string" ? sanitizeLogData(entry.message) : entry.message;

    const logEntry: RemoteLogBatchEntry = {
      level: entry.level as LogLevel,
      message: sanitizedMessage,
      context: {
        ...sanitizedContextObj,
        // Remove already included fields from context
        level: undefined,
        message: undefined
        // timestamp: undefined
      },
      timestamp: entry.timestamp,
      pathname
    };

    // Check log size
    const json = JSON.stringify(logEntry);
    const logDataSize = typeof TextEncoder !== "undefined" ? new TextEncoder().encode(json).length : json.length;
    if (logDataSize > this.MAX_LOG_SIZE) {
      console.info(
        `[LOGGER] Dropped oversized log entry (${logDataSize} bytes). Message: ${String(sanitizedMessage).slice(0, 2048)}`,
        { logEntry }
      );
      return;
    }

    // Prevent unbounded memory growth during outages.
    if (this.queue.length >= this.opts.maxQueueSize) {
      this.queue.shift();
      this.throttledConsoleWarn(
        `[LOGGER] Remote log queue full; dropping oldest log (maxQueueSize=${this.opts.maxQueueSize}).`
      );
    }

    this.queue.push(logEntry);
    this.processQueue();
  }

  private throttledConsoleWarn(message: string, extra?: unknown): void {
    const now = Date.now();
    if (now - this.lastConsoleLogAtMs < this.opts.consoleLogThrottleMs) return;
    this.lastConsoleLogAtMs = now;
    if (extra !== undefined) {
      console.warn(message, extra);
      return;
    }
    console.warn(message);
  }

  private scheduleNextAttempt(delayMs: number): void {
    if (!this.isBrowser) return;
    if (this.retryTimer) return;
    const clampedDelayMs = Math.max(0, delayMs);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = undefined;
      this.processQueue();
    }, clampedDelayMs);
  }

  private computeBackoffDelayMs(): number {
    const failures = Math.max(1, this.consecutiveFailures);
    const raw = this.opts.baseBackoffMs * Math.pow(2, failures - 1);
    const capped = Math.min(this.opts.maxBackoffMs, raw);
    const jitter = capped * this.opts.jitterRatio;
    const withJitter = capped + (Math.random() * 2 - 1) * jitter;
    return Math.max(0, Math.floor(withJitter));
  }

  private async fetchWithTimeout(url: string, init: RequestInit): Promise<Response> {
    if (typeof AbortController === "undefined") {
      return await fetch(url, init);
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.opts.fetchTimeoutMs);
    try {
      return await fetch(url, { ...init, signal: controller.signal });
    } finally {
      clearTimeout(timer);
    }
  }

  private async processQueue(): Promise<void> {
    if (!this.isBrowser || this.isSending || this.queue.length === 0) return;

    const now = Date.now();
    if (this.nextAttemptAtMs > now) {
      this.scheduleNextAttempt(this.nextAttemptAtMs - now);
      return;
    }

    this.isSending = true;
    let batch: RemoteLogBatchEntry[] = [];
    try {
      batch = this.queue.splice(0, this.opts.maxBatchSize);
      this.requestCount += batch.length;

      const response = await this.fetchWithTimeout(this.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(batch)
      });

      if (response.ok) {
        this.consecutiveFailures = 0;
        this.nextAttemptAtMs = 0;
      } else {
        this.consecutiveFailures += 1;

        // Requeue failed logs.
        if (batch.length > 0) {
          this.queue.unshift(...batch);
        }

        const inCooldown = this.consecutiveFailures >= this.opts.cooldownAfterFailures;
        const delayMs = inCooldown ? this.opts.cooldownMs : this.computeBackoffDelayMs();
        this.nextAttemptAtMs = Date.now() + delayMs;

        this.throttledConsoleWarn(
          `[LOGGER] Failed to send remote logs; retrying in ${Math.ceil(delayMs / 1000)}s (failures=${this.consecutiveFailures}, status=${response.status}).`
        );
      }
    } catch (error) {
      this.consecutiveFailures += 1;

      // Requeue failed logs
      if (batch.length > 0) {
        this.queue.unshift(...batch);
      }

      const inCooldown = this.consecutiveFailures >= this.opts.cooldownAfterFailures;
      const delayMs = inCooldown ? this.opts.cooldownMs : this.computeBackoffDelayMs();
      this.nextAttemptAtMs = Date.now() + delayMs;

      const failureMessage = error instanceof Error ? error.message : String(error);
      this.throttledConsoleWarn(
        `[LOGGER] Failed to send remote logs; retrying in ${Math.ceil(delayMs / 1000)}s (failures=${this.consecutiveFailures}, error=${failureMessage}).`
      );
    } finally {
      this.isSending = false;
      if (this.queue.length > 0) {
        const waitMs = Math.max(0, this.nextAttemptAtMs - Date.now());
        this.scheduleNextAttempt(waitMs);
      }
    }
  }
}
